import { Injectable, inject, DestroyRef } from '@angular/core';

import { ViewerCoreService } from './viewer-core.service';
import { SceneStateService } from './scene-state.service';

/**
 * Manages data streaming for profile measurements.
 */
@Injectable({ providedIn: 'root' })
export class ProfileWindowControllerService {
  private core = inject(ViewerCoreService);
  private sceneState = inject(SceneStateService);
  private destroyRef = inject(DestroyRef);

  profileWindow: { reset?: () => void; addPoints?: (pointcloud: any, points: any) => void } | null =
    null;
  profile: any = null;
  numPoints = 0;
  threshold = 60 * 1000;
  enabled = true;
  requests: any[] = [];
  scheduledRecomputeTime: number | null = null;

  private _recompute = () => {
    this.recompute();
  };

  private sceneChangedListener: ((e: any) => void) | null = null;
  private attachedViewer: any = null;

  /**
   * Attach to a viewer instance and start listening for events.
   * Safe to call multiple times — will detach from previous viewer.
   */
  attach(viewer: any): void {
    if (this.attachedViewer === viewer) return;

    this.detach();
    this.attachedViewer = viewer;

    this.sceneChangedListener = (e: any) => {
      e.oldScene?.removeEventListener('pointcloud_added', this._recompute);
      e.scene?.addEventListener('pointcloud_added', this._recompute);
    };

    viewer.addEventListener('scene_changed', this.sceneChangedListener);
    viewer.scene?.addEventListener('pointcloud_added', this._recompute);

    this.destroyRef.onDestroy(() => this.detach());
  }

  private detach(): void {
    if (!this.attachedViewer) return;

    if (this.sceneChangedListener) {
      this.attachedViewer.removeEventListener('scene_changed', this.sceneChangedListener);
    }
    this.attachedViewer.scene?.removeEventListener('pointcloud_added', this._recompute);
    this.attachedViewer = null;
    this.sceneChangedListener = null;
  }

  setProfile(profile: any): void {
    if (this.profile !== null && this.profile !== profile) {
      this.profile.removeEventListener('marker_moved', this._recompute);
      this.profile.removeEventListener('marker_added', this._recompute);
      this.profile.removeEventListener('marker_removed', this._recompute);
      this.profile.removeEventListener('width_changed', this._recompute);
    }

    this.profile = profile;

    if (this.profile) {
      this.profile.addEventListener('marker_moved', this._recompute);
      this.profile.addEventListener('marker_added', this._recompute);
      this.profile.addEventListener('marker_removed', this._recompute);
      this.profile.addEventListener('width_changed', this._recompute);
    }

    this.recompute();
  }

  reset(): void {
    if (this.profileWindow && typeof this.profileWindow.reset === 'function') {
      this.profileWindow.reset();
    }

    this.numPoints = 0;

    if (this.profile) {
      for (const request of this.requests) {
        request.cancel();
      }
    }
  }

  progressHandler(pointcloud: any, progress: any): void {
    if (this.profileWindow && typeof this.profileWindow.addPoints === 'function') {
      for (const segment of progress.segments) {
        this.profileWindow.addPoints(pointcloud, segment.points);
        this.numPoints += segment.points.numPoints;
      }
    }
  }

  cancel(): void {
    for (const request of this.requests) {
      request.cancel();
    }
    this.requests = [];
  }

  finishLevelThenCancel(): void {
    for (const request of this.requests) {
      request.finishLevelThenCancel();
    }
    this.requests = [];
  }

  recompute(): void {
    if (!this.profile) {
      return;
    }

    if (
      this.scheduledRecomputeTime !== null &&
      this.scheduledRecomputeTime > new Date().getTime()
    ) {
      return;
    } else {
      this.scheduledRecomputeTime = new Date().getTime() + 100;
    }
    this.scheduledRecomputeTime = null;

    this.reset();

    const pointclouds = this.sceneState.pointclouds();
    if (!pointclouds.length) return;

    for (const pointcloud of pointclouds.filter((p: any) => p.visible)) {
      const request = pointcloud.getPointsInProfile(this.profile, null, {
        onProgress: (event: any) => {
          if (!this.enabled) {
            return;
          }

          this.progressHandler(pointcloud, event.points);

          if (this.numPoints > this.threshold) {
            this.finishLevelThenCancel();
          }
        },
        onFinish: (_event: any) => {
          // no-op
        },
        onCancel: () => {
          // no-op
        },
      });

      this.requests.push(request);
    }
  }
}
